import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';

import { formatCalendarDay } from 'support/helpers/dateTime/dateTime';
import { FilterValue, PropertyFilter } from 'support/types/filters';
import { Badge } from 'components/Display/Badge/Badge';

export const FilterBadges = ({
  filters,
  remove,
  filtersConfig,
}: {
  filters: Array<FilterValue>;
  remove: (key: string, value: string) => void;
  filtersConfig: Array<PropertyFilter>;
}) => {
  const { t } = useTranslation();
  const filteredFilters = useMemo(() => filters.filter((filter) => filter.value), [filters]);

  if (!filteredFilters.length) {
    return null;
  }
  return (
    <div className="flex flex-wrap items-center gap-2">
      <span className="text-xs text-gray-500">{t('common:components.filters.filteredBy')}</span>
      {filteredFilters.map((filter) => (
        <Badge
          key={`${filter.key}+${filter.value}`}
          type="filters"
          className="max-w-xs"
          onRemove={
            isDefaultValue(filtersConfig, filter)
              ? undefined
              : () => {
                  remove(filter.key, filter.value);
                }
          }
        >
          {getFilterLabel(filtersConfig, filter)}: {getValueLabel(filtersConfig, filter)}
        </Badge>
      ))}
    </div>
  );
};

const isDefaultValue = (defaultValues: Array<PropertyFilter>, filter: FilterValue) => {
  return defaultValues.some(
    (defaultValue) =>
      defaultValue.key === filter.key &&
      defaultValue.dropDownOptions?.defaultToFirstValue &&
      filter.value === defaultValue.dropDownOptions.dropdownValues[0].label,
  );
};

const formatDate = (date: string): string => {
  const [fromDate, toDate] = date.split(',');
  return `${formatCalendarDay(fromDate)} - ${formatCalendarDay(toDate)}`;
};

const getFilterLabel = (filtersConfig: Array<PropertyFilter>, filter: FilterValue): string => {
  const filterConfig = filtersConfig.find((filterConfig) => filterConfig.key === filter.key);
  const placeholder = filterConfig?.placeholder;
  return filterConfig?.label || (typeof placeholder === 'string' ? placeholder : undefined) || filter.key;
};

const isDate = (filtersConfig: Array<PropertyFilter>, filter: FilterValue) => {
  return filtersConfig.some((filterConfig) => filterConfig.key === filter.key && filterConfig.type === 'date');
};

const getValueLabel = (filtersConfig: Array<PropertyFilter>, filter: FilterValue): string => {
  if (isDate(filtersConfig, filter)) {
    return formatDate(filter.value);
  }

  const options = filtersConfig.find((filterConfig) => filterConfig.key === filter.key)?.dropDownOptions;

  // Sub-option values look like "INVOICE" (default) or "ORDER:INBOUND" (sub-option selected).
  if (options?.subOptions) {
    const [value, subOptionValue] = filter.value.split(':');
    const valueLabel = options.dropdownValues.find((dropdownValue) => dropdownValue.value === value)?.label || value;
    const subLabel = options.subOptions.values.find((subOption) => subOption.value === subOptionValue)?.label;

    return subLabel ? `${valueLabel} (${subLabel})` : valueLabel;
  }

  return options?.dropdownValues.find((dropdownValue) => dropdownValue.value === filter.value)?.label || filter.value;
};
